/**
 * The project Tasks page — hosted private tasks v1, P6; redesigned after the
 * P9 staging run (2026-09-29).
 *
 * An owner writes a prompt, picks one of their saved provider keys, a model
 * the price book prices, and a spend limit, then approves that one run with
 * "Approve & run". The approval card above the button lists exactly what is
 * being approved; the server mints a single-run approval bound to those values
 * and queues the task, and a worker runs it with the owner's own key. Nothing
 * here holds an approval id or key material: keys show only `masked_hint`.
 *
 * The list refreshes on its own while any task is still queued or running, and
 * shows each finished run's duration, cost and how many approved project facts
 * it used. The answer is filed as a memory candidate; the owner keeps it as a
 * project fact or discards it right here, through the same review route
 * Project Brain uses.
 */

import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'wouter';

import { fetchPendingMemoryCandidates, reviewMemoryCandidate } from '../../api/brain';
import { HelpTip } from '../../components/HelpTip';
import {
  HostedTask,
  MAX_SPEND_CEILING_MICROS,
  SPEND_PRESETS_MICROS,
  TaskModelOption,
  TaskOptions,
  formatMicros,
  formatUsd,
  getTaskOptions,
  isFinished,
  listTasks,
  submitTask,
  taskCandidateId,
  taskElapsedSeconds,
  taskErrorMessage,
  taskFailureMessage,
  taskOutputText,
  taskRecallCount,
  taskSpendUsd,
  typicalCost,
  usdToMicros,
} from '../../api/tasks';
import { listProviderKeys } from '../ProviderKeys/providerKeysApi';
import { ModelPicker, defaultModel, perMtok } from './ModelPicker';

/** Poll while anything is queued or running; a run usually takes 10–30 s. */
export const POLL_INTERVAL_MS = 2000;

export interface ProviderKeyOption {
  key_id: string;
  provider: string;
  label: string;
  masked_hint: string;
}

export function providerKeyOptions(payload: unknown): ProviderKeyOption[] {
  const record = (payload ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(record.provider_keys) ? record.provider_keys
    : Array.isArray(record.keys) ? record.keys : [];
  return rows
    .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
    .filter((row) => typeof row.key_id === 'string' && typeof row.provider === 'string')
    .map((row) => ({
      key_id: String(row.key_id),
      provider: String(row.provider),
      label: typeof row.label === 'string' ? row.label : '',
      masked_hint: typeof row.masked_hint === 'string' ? row.masked_hint : '',
    }));
}

function keyName(key: ProviderKeyOption): string {
  return `${key.provider} · ${key.label || key.masked_hint}`;
}

export function modelLabel(model: TaskModelOption): string {
  return `${model.model} · ${perMtok(model.input_per_mtok)} / ${perMtok(model.output_per_mtok)}`;
}

type TaskState = 'queued' | 'running' | 'done' | 'failed';

function taskState(task: HostedTask): TaskState {
  if (task.status === 'succeeded') return 'done';
  if (task.failed || task.status === 'dead_letter' || task.status === 'failed' || task.status === 'cancelled') return 'failed';
  if (task.status === 'claimed') return 'running';
  return 'queued';
}

const STATE_LABEL: Record<TaskState, string> = {
  queued: 'Queued', running: 'Running', done: 'Done', failed: "Didn't finish",
};

export type ReviewOutcome = 'kept' | 'discarded';

export function TaskList({ projectId, tasks, error, pendingCandidates = null, reviewed = {}, onReview, now }: {
  projectId: string;
  tasks: HostedTask[] | null;
  error: string;
  /** Candidate ids still awaiting review; `null` when unknown (review then links to Project Brain). */
  pendingCandidates?: ReadonlySet<string> | null;
  reviewed?: Readonly<Record<string, ReviewOutcome | 'busy' | 'error'>>;
  onReview?: (candidateId: string, decision: 'approve' | 'reject') => void;
  now?: number;
}): React.ReactElement {
  if (error) return <p role="alert" className="error">{error}</p>;
  if (tasks === null) return <p className="task-empty">Loading tasks…</p>;
  if (tasks.length === 0) return <p className="task-empty">No tasks yet. Your runs will appear here.</p>;
  const brain = `/projects/${encodeURIComponent(projectId)}/brain`;
  return (
    <ol className="task-list" aria-label="Tasks">
      {tasks.map((task) => {
        const state = taskState(task);
        const output = taskOutputText(task);
        const candidate = taskCandidateId(task);
        const spend = taskSpendUsd(task);
        const elapsed = taskElapsedSeconds(task, now);
        const facts = taskRecallCount(task);
        const outcome = candidate ? reviewed[candidate] : undefined;
        return (
          <li key={task.task_id} className="task-item">
            <div className="task-top">
              <span className={`task-state task-state-${state}`}>{STATE_LABEL[state]}</span>
              <span className="task-meta">
                <span>{task.approval.model}{task.approval.reasoning_effort ? ` · ${task.approval.reasoning_effort}` : ''}</span>
                {elapsed !== null && <span>{elapsed} s</span>}
                {state === 'done' && spend !== null && <span>{formatUsd(spend)}</span>}
                {state !== 'done' && <span>limit {formatMicros(task.approval.spend_ceiling_micros)}</span>}
              </span>
            </div>
            <p className="task-prompt">{task.prompt}</p>
            {output && <div className="task-output">{output}</div>}
            {state === 'done' && facts > 0 && (
              <p className="task-facts">
                <Link to={brain} className="task-fact-chip">Used {facts} project fact{facts === 1 ? '' : 's'}</Link>
                <HelpTip topic="Used project facts">
                  Before answering, the model was given this many of your project's approved facts that
                  matched the prompt. That keeps answers consistent with what you have already decided.
                  Open Project Brain to see them.
                </HelpTip>
              </p>
            )}
            {candidate && state === 'done' && (
              outcome === 'kept' ? <p className="task-review task-review-done">Saved as a project fact.</p>
              : outcome === 'discarded' ? <p className="task-review">Discarded. It won't be recalled.</p>
              : pendingCandidates === null || !onReview ? (
                <p className="task-review">
                  Saved for review. <Link to={brain}>Review in Project Brain</Link> before it can become a project fact.
                </p>
              ) : pendingCandidates.has(candidate) ? (
                <div className="task-review" role="group" aria-label="Keep this answer?">
                  <span>Keep this as a project fact?</span>
                  <HelpTip topic="Save as fact">
                    Save as fact turns this answer into a project fact that future tasks can use. Discard
                    keeps it out of memory. Nothing becomes a fact until you choose. You can review or
                    retire facts later in Project Brain.
                  </HelpTip>
                  <button type="button" disabled={outcome === 'busy'} onClick={() => onReview(candidate, 'approve')}>Save as fact</button>
                  <button type="button" className="secondary" disabled={outcome === 'busy'} onClick={() => onReview(candidate, 'reject')}>Discard</button>
                  {outcome === 'error' && <span className="error">That didn't save. Try again, or use Project Brain.</span>}
                </div>
              ) : null
            )}
            {state === 'failed' && !output && (
              <p className="error task-failure">{taskFailureMessage(task.failure, task.approval.provider)}</p>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export function ApprovalCard({ keyOption, modelId, ceilingMicros, typical, disabled, busy }: {
  keyOption: ProviderKeyOption | null;
  modelId: string;
  ceilingMicros: number | null;
  typical: ReturnType<typeof typicalCost>;
  disabled: boolean;
  busy: boolean;
}): React.ReactElement {
  return (
    <div className="approval-card" aria-live="polite">
      <div className="field-head approval-head">
        <strong>You are approving</strong>
        <HelpTip topic="the approval">
          Nothing runs until you press Approve &amp; run. Your approval covers exactly this key, model
          and limit, is used once, and expires after 15 minutes. Change any of them and you are
          approving a different run.
        </HelpTip>
      </div>
      <dl>
        <dt>Key</dt><dd>{keyOption ? keyName(keyOption) : '—'}</dd>
        <dt>Model</dt><dd>{modelId || '—'}</dd>
        <dt>Most it can cost</dt><dd>{ceilingMicros === null ? '—' : formatMicros(ceilingMicros)}</dd>
        <dt>
          Usually costs{' '}
          <HelpTip topic="usual cost">
            The average of this project's earlier runs on this model, when there are any. Otherwise an
            estimate from the model's price and the length of your prompt. The real cost is shown on
            the task when it finishes.
          </HelpTip>
        </dt>
        <dd>
          {typical === null ? '—'
            : typical.basis === 'history'
              ? `about ${formatUsd(typical.usd)} (average of ${typical.runs} run${typical.runs === 1 ? '' : 's'})`
              : `about ${formatUsd(typical.usd)} (estimate)`}
        </dd>
        <dt>
          Uses{' '}
          <HelpTip topic="project facts">
            Before the model answers, Auteur looks up the approved facts in this project that match your
            prompt and includes them, so answers stay consistent with what you have already decided.
            Only facts someone approved are used.
          </HelpTip>
        </dt>
        <dd>approved project facts that match your prompt</dd>
      </dl>
      <button type="submit" disabled={disabled}>{busy ? 'Starting…' : 'Approve & run'}</button>
      <p className="approval-fine">
        One run, one attempt. It is never retried, and it cannot spend more than the limit.
        The approval expires in 15 minutes.
      </p>
    </div>
  );
}

function FirstRun(): React.ReactElement {
  return (
    <div className="task-first-run">
      <strong>Connect a model provider to run tasks</strong>
      <ol>
        <li>Add a provider key (OpenAI, Anthropic or OpenRouter) in <Link to="/settings/provider-keys">Settings → Provider keys</Link>. The key stays in your vault and is never shown again.</li>
        <li>Come back here, write a prompt, and approve the run. A short task on a small model costs well under a cent.</li>
      </ol>
      <Link to="/settings/provider-keys" className="button-link">Add a provider key</Link>
    </div>
  );
}

export function ThinkingLevel({ efforts, value, onChange }: {
  efforts: readonly string[];
  value: string;
  onChange: (effort: string) => void;
}): React.ReactElement | null {
  if (!efforts.length) return null;
  return (
    <div className="field">
      <label htmlFor="task-effort">Thinking level</label>
      <select id="task-effort" value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">Provider default</option>
        {efforts.map((effort) => <option key={effort} value={effort}>{effort}</option>)}
      </select>
      <p className="field-hint">More thinking can take longer and cost more. Your spend limit still applies.</p>
    </div>
  );
}

export function TasksPage({ projectId }: { projectId: string }): React.ReactElement {
  const [keys, setKeys] = useState<ProviderKeyOption[] | null>(null);
  const [options, setOptions] = useState<TaskOptions | null>(null);
  const [tasks, setTasks] = useState<HostedTask[] | null>(null);
  const [pending, setPending] = useState<ReadonlySet<string> | null>(null);
  const [reviewed, setReviewed] = useState<Record<string, ReviewOutcome | 'busy' | 'error'>>({});
  const [listError, setListError] = useState('');
  const [prompt, setPrompt] = useState('');
  const [keyId, setKeyId] = useState('');
  const [model, setModel] = useState('');
  const [reasoningEffort, setReasoningEffort] = useState('');
  const [limitMicros, setLimitMicros] = useState<number | null>(250_000);
  const [customLimit, setCustomLimit] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [now, setNow] = useState(() => Date.now());

  const selectedKey = useMemo(() => keys?.find((key) => key.key_id === keyId) ?? null, [keys, keyId]);
  const models = useMemo(
    () => (options?.models ?? []).filter((option) => option.provider === selectedKey?.provider),
    [options, selectedKey],
  );
  const selectedModel = models.find((option) => option.model === model) ?? null;
  const supportedEfforts = selectedModel?.supported_reasoning_efforts ?? [];
  useEffect(() => {
    if (reasoningEffort && !supportedEfforts.includes(reasoningEffort)) setReasoningEffort('');
  }, [selectedModel, reasoningEffort]);
  const custom = customLimit.trim() ? usdToMicros(customLimit) : null;
  const ceiling = custom ? ('error' in custom ? null : custom.micros) : limitMicros;
  const maxCeiling = options?.max_spend_ceiling_micros ?? MAX_SPEND_CEILING_MICROS;
  const typical = typicalCost(selectedModel, model.trim(), prompt.length, tasks ?? []);

  const refreshPending = useCallback(async () => {
    try {
      const rows: unknown = await fetchPendingMemoryCandidates(projectId);
      const list = Array.isArray(rows) ? rows
        : Array.isArray((rows as { candidates?: unknown })?.candidates) ? (rows as { candidates: unknown[] }).candidates : null;
      setPending(list === null ? null : new Set(list
        .map((row) => (row as { candidate_id?: unknown })?.candidate_id)
        .filter((id): id is string => typeof id === 'string')));
    } catch {
      setPending(null);
    }
  }, [projectId]);

  const refresh = useCallback(async () => {
    try {
      setTasks(await listTasks(projectId));
      setListError('');
      setNow(Date.now());
    } catch (failure) {
      setListError(taskErrorMessage(failure));
    }
  }, [projectId]);

  useEffect(() => {
    listProviderKeys().then(
      (payload: unknown) => {
        const found = providerKeyOptions(payload);
        setKeys(found);
        if (found.length === 1) setKeyId(found[0].key_id);
      },
      () => setKeys([]),
    );
    getTaskOptions(projectId).then(setOptions, () => setOptions(null));
    void refresh();
    void refreshPending();
  }, [projectId, refresh, refreshPending]);

  // Start on the first visible option (Featured, then Tested by Auteur, then the cheapest).
  useEffect(() => {
    if (models.length > 0 && !models.some((option) => option.model === model)) {
      setModel(defaultModel(models)?.model ?? '');
    }
  }, [models, model]);

  const open = (tasks ?? []).some((task) => !isFinished(task));
  useEffect(() => {
    if (!open) return undefined;
    const timer = setInterval(() => { void refresh(); }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [open, refresh]);
  // A run that just finished filed a new candidate: learn that it is pending.
  const finishedCount = (tasks ?? []).filter(isFinished).length;
  useEffect(() => { void refreshPending(); }, [finishedCount, refreshPending]);

  async function review(candidateId: string, decision: 'approve' | 'reject') {
    setReviewed((state) => ({ ...state, [candidateId]: 'busy' }));
    try {
      await reviewMemoryCandidate(candidateId, decision, undefined, projectId);
      setReviewed((state) => ({ ...state, [candidateId]: decision === 'approve' ? 'kept' : 'discarded' }));
    } catch {
      setReviewed((state) => ({ ...state, [candidateId]: 'error' }));
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!selectedKey || ceiling === null || !model.trim() || !prompt.trim()) return;
    setBusy(true);
    setFormError('');
    try {
      await submitTask(projectId, {
        prompt,
        provider: selectedKey.provider,
        model: model.trim(),
        ...(reasoningEffort ? { reasoningEffort } : {}),
        vaultKeyId: selectedKey.key_id,
        spendCeilingMicros: ceiling,
        idempotencyKey: crypto.randomUUID(),
      });
      setPrompt('');
      await refresh();
    } catch (failure) {
      setFormError(taskErrorMessage(failure));
    } finally {
      setBusy(false);
    }
  }

  const spentRecent = (tasks ?? []).map(taskSpendUsd).filter((usd): usd is number => typeof usd === 'number')
    .reduce((a, b) => a + b, 0);
  const noKeys = keys !== null && keys.length === 0;

  return (
    <section aria-label="Hosted tasks" className="tasks-page">
      <div className="tasks-head">
        <div>
          <p className="eyebrow">Project</p>
          <h1>Tasks</h1>
          <p className="page-note">Run a prompt on your own AI provider key, with your project's approved facts. You approve each run and its spend limit.</p>
        </div>
        <dl className="tasks-budget" aria-label="Spending">
          <div>
            <dt>Spent on recent tasks <HelpTip topic="recent spend">What your most recent tasks in this project (up to 50) actually cost, as reported by the provider. The provider bills this to the account behind your key.</HelpTip></dt>
            <dd>{tasks === null ? '—' : formatUsd(spentRecent)}</dd>
          </div>
          <div>
            <dt>Per-run limit <HelpTip topic="the per-run limit">The highest spend limit any single run can have during the beta. You choose the actual limit for each run below.</HelpTip></dt>
            <dd>up to {formatMicros(maxCeiling)}</dd>
          </div>
          {options?.price_book && (
            <div>
              <dt>Prices as of <HelpTip topic="the price date">The date the model prices were last checked. Auteur uses these prices to keep every run under its limit.</HelpTip></dt>
              <dd>{options.price_book.as_of}</dd>
            </div>
          )}
        </dl>
      </div>

      <div className="tasks-grid">
        <div>
          {noKeys ? <FirstRun /> : (
            <form onSubmit={submit} className="task-form">
              <div className="field">
                <div className="field-head">
                  <label htmlFor="task-prompt">What should the model do?</label>
                  <HelpTip topic="the prompt">
                    Tell the model what you want, the way you would brief a person. Auteur adds your
                    project's approved facts for you, so you don't need to repeat them. Up to 20,000
                    characters.
                  </HelpTip>
                </div>
                <textarea id="task-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} required maxLength={20000} />
              </div>
              <div className="task-form-row">
                <div className="field">
                  <div className="field-head">
                    <label htmlFor="task-key">Provider key</label>
                    <HelpTip topic="a provider key">
                      Which of your AI accounts pays for this run: OpenAI, Anthropic or OpenRouter. Keys you add
                      in Settings are stored encrypted in your vault, and only the last few characters are
                      ever shown. The provider bills that account directly.
                    </HelpTip>
                  </div>
                  <select id="task-key" value={keyId} onChange={(e) => { setKeyId(e.target.value); setModel(''); }} required>
                    <option value="">Choose a key</option>
                    {(keys ?? []).map((key) => <option key={key.key_id} value={key.key_id}>{keyName(key)}</option>)}
                  </select>
                </div>
              </div>
              <ModelPicker
                models={models}
                allModels={options?.models ?? []}
                providersWithKeys={new Set((keys ?? []).map((key) => key.provider))}
                value={selectedKey && model ? `${selectedKey.provider}/${model}` : ''}
                onChange={(option) => setModel(option.model)}
                onUseOther={(option) => {
                  const match = (keys ?? []).find((key) => key.provider === option.provider);
                  if (match) { setKeyId(match.key_id); setModel(option.model); }
                }}
                dossierNote={options?.dossier_note}
                emptyMessage={
                  !selectedKey ? 'Choose a key first.'
                    : options === null ? 'The model list could not be loaded. Reload the page to try again.'
                    : `No priced models for ${selectedKey.provider} yet. Pick a key from another provider, or browse all models.`
                }
              />
              <ThinkingLevel efforts={supportedEfforts} value={reasoningEffort} onChange={setReasoningEffort} />
              <fieldset className="task-limit">
                <legend className="field-head">
                  <span>Most this run may spend</span>
                  <HelpTip topic="the spend limit">
                    The most this one run is allowed to cost. Auteur shortens the model's longest possible
                    answer so the worst case fits under this amount. If even a short answer can't fit, the
                    run stops before calling the model and costs nothing. Most short tasks cost a small
                    fraction of a cent.
                  </HelpTip>
                </legend>
                <div className="limit-chips">
                  {SPEND_PRESETS_MICROS.filter((micros) => micros <= maxCeiling).map((micros) => (
                    <button key={micros} type="button" className="chip"
                            aria-pressed={!customLimit.trim() && limitMicros === micros}
                            onClick={() => { setLimitMicros(micros); setCustomLimit(''); }}>
                      {formatMicros(micros)}
                    </button>
                  ))}
                  <label className="limit-custom">Other
                    <input value={customLimit} onChange={(e) => setCustomLimit(e.target.value)} inputMode="decimal" placeholder="0.10" />
                  </label>
                </div>
                {custom && 'error' in custom && <p className="error">{custom.error}</p>}
              </fieldset>
              <ApprovalCard
                keyOption={selectedKey}
                modelId={reasoningEffort ? `${model.trim()} · ${reasoningEffort}` : model.trim()}
                ceilingMicros={ceiling}
                typical={typical}
                busy={busy}
                disabled={busy || !selectedKey || !model.trim() || ceiling === null || !prompt.trim()}
              />
              {formError && <p role="alert" className="error">{formError}</p>}
            </form>
          )}
        </div>
        <div>
          <div className="field-head tasks-list-head">
            <h2 className="tasks-list-heading">Recent tasks</h2>
            <HelpTip topic="recent tasks">
              Every run you start appears here. Queued: waiting for a worker. Running: the model is
              answering. Done: finished, with how long it took and what it cost. Didn't finish: it
              stopped, and the reason is shown. A run is never retried, so it never spends twice.
            </HelpTip>
          </div>
          <TaskList
            projectId={projectId}
            tasks={tasks}
            error={listError}
            pendingCandidates={pending}
            reviewed={reviewed}
            onReview={(id, decision) => { void review(id, decision); }}
            now={now}
          />
        </div>
      </div>
    </section>
  );
}

export default TasksPage;
